/**
 * Product sizes are ordinary product add-on rows that share a
 * `variant_group_id`; each row's `name` is the composed
 * "<Product> — <Label>". These pure helpers turn a flat add-on list into
 * products with sizes for display. Client-safe (no server imports).
 */

export const SIZE_LABEL_SEPARATOR = ' — ';

export const PRESET_SIZE_LABELS = ['S', 'M', 'L', 'XL', 'XXL'] as const;

export interface AddonSizeKey {
  groupId: string;
  label: string;
}

export type AddonSizeEntry<T> =
  | { kind: 'single'; addon: T }
  | {
      kind: 'group';
      groupId: string;
      baseName: string;
      sizes: T[];
      minPrice: number;
    };

export function composeSizedName(baseName: string, label: string): string {
  return `${baseName.trim()}${SIZE_LABEL_SEPARATOR}${label.trim()}`;
}

/** "Academy Kit — M" + "M" → "Academy Kit". Unsized names pass through. */
export function getAddonBaseName(
  name: string,
  label: string | null | undefined,
): string {
  const trimmedLabel = label?.trim();

  if (!trimmedLabel) return name;

  const suffix = `${SIZE_LABEL_SEPARATOR}${trimmedLabel}`;

  return name.endsWith(suffix) ? name.slice(0, -suffix.length) : name;
}

/** Preset sizes first (S → XXL), then any custom label alphabetically. */
export function compareSizeLabels(a: string, b: string): number {
  const rank = (label: string) => {
    const index = PRESET_SIZE_LABELS.indexOf(
      label.trim().toUpperCase() as (typeof PRESET_SIZE_LABELS)[number],
    );

    return index === -1 ? PRESET_SIZE_LABELS.length : index;
  };

  return rank(a) - rank(b) || a.localeCompare(b);
}

/**
 * Groups sibling sizes into one entry, keeping the order in which each
 * product first appears. A group with a single visible size collapses to a
 * plain row unless `keepSingleSizeGroups` is set (the admin catalog keeps it
 * so the product can still be edited as a sized product).
 */
export function groupAddonsBySize<
  T extends { id: string; name: string; price: number },
>(
  addons: T[],
  sizeOf: (addon: T) => AddonSizeKey | null,
  options: { keepSingleSizeGroups?: boolean } = {},
): AddonSizeEntry<T>[] {
  type GroupDraft = { groupId: string; first: T; sizes: T[] };

  const entries: Array<{ addon: T } | GroupDraft> = [];
  const groupsById = new Map<string, GroupDraft>();

  for (const addon of addons) {
    const size = sizeOf(addon);

    if (!size) {
      entries.push({ addon });
      continue;
    }

    const existing = groupsById.get(size.groupId);

    if (existing) {
      existing.sizes.push(addon);
      continue;
    }

    const group = { groupId: size.groupId, first: addon, sizes: [addon] };

    groupsById.set(size.groupId, group);
    entries.push(group);
  }

  return entries.map((entry): AddonSizeEntry<T> => {
    if ('addon' in entry) return { kind: 'single', addon: entry.addon };

    if (entry.sizes.length === 1 && !options.keepSingleSizeGroups) {
      return { kind: 'single', addon: entry.first };
    }

    const labelOf = (addon: T) => sizeOf(addon)?.label ?? '';
    const sizes = [...entry.sizes].sort((a, b) =>
      compareSizeLabels(labelOf(a), labelOf(b)),
    );
    const sample = sizes[0] ?? entry.first;

    return {
      kind: 'group',
      groupId: entry.groupId,
      baseName: getAddonBaseName(sample.name, labelOf(sample)),
      sizes,
      minPrice: Math.min(...sizes.map((size) => Number(size.price))),
    };
  });
}

/** `sizeOf` for rows that carry the raw `booking_addons` columns. */
export function sizeOfAddonRow(addon: {
  variant_group_id?: string | null;
  variant_label?: string | null;
}): AddonSizeKey | null {
  return addon.variant_group_id && addon.variant_label
    ? { groupId: addon.variant_group_id, label: addon.variant_label }
    : null;
}
